import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Upload, OpenInNew, Description, CheckCircle, Info } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Club } from '../../types';

const ImportCardsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [sheetUrl, setSheetUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      const response = await api.get<Club[]>('/clubs');
      setClubs(response.data);
    } catch (error) {
      console.error('Failed to fetch clubs:', error);
      setError('Failed to load clubs');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Convert Google Sheets URL to export format if needed
      let exportUrl = sheetUrl;
      
      // Check if it's a Google Sheets URL that needs conversion
      if (sheetUrl.includes('docs.google.com/spreadsheets') && !sheetUrl.includes('/export?format=csv')) {
        // Extract the spreadsheet ID using regex
        const match = sheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
        if (match) {
          exportUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=csv`;
        }
      }
      
      const response = await api.post('/import-data', {
        club_id: selectedClub,
        data_url: exportUrl,
      });

      setSuccess(t('cards.import.successMessage', { count: response.data.count }));
      setTimeout(() => {
        navigate('/app/cards');
      }, 2000);
    } catch (error: any) {
      let errorMessage = t('cards.import.errorMessage');
      
      if (error.response?.data?.detail) {
        // Handle string error messages
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } 
        // Handle validation error arrays
        else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map((err: any) => err.msg || err.message || JSON.stringify(err))
            .join(', ');
        }
        // Handle object errors
        else if (typeof error.response.data.detail === 'object') {
          errorMessage = error.response.data.detail.msg || 
                        error.response.data.detail.message || 
                        JSON.stringify(error.response.data.detail);
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h4" sx={{ mb: 4 }}>
        {t('cards.import.title')}
      </Typography>

      {/* Template Information Card */}
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'info.light', borderColor: 'info.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Description color="info" />
            <Typography variant="h6">
              {t('cards.import.templateTitle')}
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('cards.import.templateDescription')}
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t('cards.import.languageSupport')}
            </Typography>
          </Alert>
          
          <List dense sx={{ mb: 2 }}>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={<strong>{t('cards.import.mandatoryFields.id')}</strong>}
                secondary={t('cards.import.mandatoryFields.idDescription')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={<strong>{t('cards.import.mandatoryFields.firstName')} (Nome/Name)</strong>}
                secondary={t('cards.import.mandatoryFields.firstNameDescription')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={<strong>{t('cards.import.mandatoryFields.lastName')} (Cognome/Surname)</strong>}
                secondary={t('cards.import.mandatoryFields.lastNameDescription')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={<strong>{t('cards.import.mandatoryFields.email')}</strong>}
                secondary={t('cards.import.mandatoryFields.emailDescription')}
              />
            </ListItem>
          </List>
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('cards.import.optionalFieldsNote')}
          </Typography>
          
          <Button
            variant="contained"
            color="info"
            startIcon={<OpenInNew />}
            href="https://docs.google.com/spreadsheets/d/1Wu3F824euVaT9cCk92QvuwfnsI9Thf2GWelBsEogpZE/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
          >
            {t('cards.import.viewTemplate')}
          </Button>
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
            {t('cards.import.copyTemplateNote')}
          </Typography>
        </CardContent>
      </Card>

      <Paper sx={{ p: 4 }}>

        <Box component="form" onSubmit={handleSubmit}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('cards.import.selectClub')}</InputLabel>
            <Select
              value={selectedClub}
              onChange={(e) => setSelectedClub(e.target.value)}
              label={t('cards.import.selectClub')}
              required
            >
              {clubs.map((club) => (
                <MenuItem key={club.record_id} value={club.record_id}>
                  {club.club_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t('cards.import.googleSheetsUrl')}
            value={sheetUrl}
            onChange={(e) => setSheetUrl(e.target.value)}
            placeholder={t('cards.import.urlPlaceholder')}
            required
            sx={{ mb: 3 }}
            helperText={t('cards.import.urlHelperText')}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Upload />}
              disabled={loading || !selectedClub || !sheetUrl}
            >
              {loading ? t('cards.import.importing') : t('cards.import.importButton')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/app/cards')}
              disabled={loading}
            >
              {t('cards.import.cancel')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          <Info sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('cards.import.howToTitle')}
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <Chip label="1" size="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary={t('cards.import.steps.prepare')}
              secondary={t('cards.import.steps.prepareDescription')}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Chip label="2" size="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary={t('cards.import.steps.share')}
              secondary={t('cards.import.steps.shareDescription')}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Chip label="3" size="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary={t('cards.import.steps.copyUrl')}
              secondary={t('cards.import.steps.copyUrlDescription')}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Chip label="4" size="small" color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary={t('cards.import.steps.import')}
              secondary={t('cards.import.steps.importDescription')}
            />
          </ListItem>
        </List>
      </Paper>
    </Container>
  );
};

export default ImportCardsPage;